/**
 * sms-signup.js
 * Shared phone/email subscription logic, extracted from the footer newsletter so the loyalty
 * "ways to earn" popups sign a customer up exactly the way the footer does.
 *
 * Classic script (not a module) because `footer.js` is loaded with `script_tag` and cannot
 * import. Attaches `window.BWW.smsSignup` and is safe to load more than once per page.
 *
 * Neither endpoint is called with credentials from the browser: the SMS endpoint is a back-end
 * proxy holding the Postscript API key, and the email endpoint is the BWW middleware holding the
 * Klaviyo list id. See scripts/bww-middleware/README.md.
 *
 * @version 1.0.0
 */
(function () {
  "use strict";

  window.BWW = window.BWW || {};
  // Idempotent: two sections may both request this asset.
  if (window.BWW.smsSignup) return;

  /**
   * Progressive US formatting — `(123) 456-7890` — applied as the customer types.
   * @param {string} raw
   * @returns {string}
   */
  function formatPhoneValue(raw) {
    const value = String(raw == null ? "" : raw).replace(/\D/g, "");

    if (value.length === 0) return "";
    if (value.length <= 3) return `(${value}`;
    if (value.length <= 6) return `(${value.slice(0, 3)}) ${value.slice(3)}`;
    return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
  }

  /**
   * Blocks any keystroke that is not a digit or an editing/navigation key, so the formatted
   * value never has to recover from stray characters.
   * @param {KeyboardEvent} event
   */
  function handlePhoneKeydown(event) {
    // Allow backspace, delete, tab, escape, enter
    if (
      [8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true)
    ) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((event.shiftKey || event.keyCode < 48 || event.keyCode > 57) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }

  /**
   * Wires progressive formatting and digit-only entry onto a phone field.
   * @param {HTMLInputElement} input
   */
  function attachPhoneFormatting(input) {
    if (!input || input.dataset.phoneFormatting === "true") return;
    input.dataset.phoneFormatting = "true";
    input.addEventListener("input", () => {
      input.value = formatPhoneValue(input.value);
    });
    input.addEventListener("keydown", handlePhoneKeydown);
  }

  /**
   * @param {string} digits - Phone number stripped to digits.
   * @returns {boolean}
   */
  function isValidPhone(digits) {
    // US phone number validation (10 digits)
    return /^[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(String(digits == null ? "" : digits));
  }

  /**
   * @param {string} email
   * @returns {boolean}
   */
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email == null ? "" : email));
  }

  /** @param {string} value @returns {string} */
  function digitsOnly(value) {
    return String(value == null ? "" : value).replace(/\D/g, "");
  }

  /**
   * Subscribes a phone number through the Postscript proxy.
   * @param {{ endpoint: string, phone: string, keywordId: string }} opts
   * @returns {Promise<{ ok: boolean, message?: string }>}
   */
  async function subscribePhone(opts) {
    const endpoint = opts && opts.endpoint;
    if (!endpoint) return { ok: false, message: "missing endpoint" };

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { accept: "application/json", "content-type": "application/json" },
        body: JSON.stringify({
          phone_number: digitsOnly(opts.phone),
          keyword_id: opts.keywordId,
          origin: "website",
        }),
      });
      // The proxy answers with a bare status for SMS, so `ok` is the whole signal.
      return { ok: res.ok };
    } catch (error) {
      console.error("[sms-signup] phone subscribe failed", error);
      return { ok: false };
    }
  }

  /**
   * Subscribes an email address through the BWW middleware.
   * @param {{ endpoint: string, email: string }} opts
   * @returns {Promise<{ ok: boolean, message?: string }>}
   */
  async function subscribeEmail(opts) {
    const endpoint = opts && opts.endpoint;
    if (!endpoint) return { ok: false, message: "missing endpoint" };

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { accept: "application/json", "content-type": "application/json" },
        body: JSON.stringify({ email: String(opts.email == null ? "" : opts.email).trim() }),
      });
      const data = await res.json().catch(() => ({}));
      // The middleware reports its own outcome in the body, so `success` is the signal.
      return { ok: data.success === true, message: data.message };
    } catch (error) {
      console.error("[sms-signup] email subscribe failed", error);
      return { ok: false };
    }
  }

  window.BWW.smsSignup = {
    formatPhoneValue,
    handlePhoneKeydown,
    attachPhoneFormatting,
    isValidPhone,
    isValidEmail,
    digitsOnly,
    subscribePhone,
    subscribeEmail,
  };
})();
